// ErrorBoundary.tsx
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * Props for the ErrorBoundary
 */
interface ErrorBoundaryProps {
  /** Components protected by the boundary */
  children: ReactNode;

  /**
   * Fallback UI renderer
   * Receives the error and a reset function for recovery
   */
  fallback: (props: { error: Error; resetError: () => void }) => ReactNode;

  /**
   * Optional error side-effect hook
   * Useful for logging / monitoring (Sentry, Datadog, etc.)
   */
  onError?: (error: Error, info: ErrorInfo) => void;

  /**
   * Optional keys that automatically reset the boundary
   * Example: route change, user change, locale change
   */
  resetKeys?: unknown[];
}

/**
 * Internal state of the ErrorBoundary
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary
 *
 * Purpose:
 * - Catch render-time errors in the React tree
 * - Prevent the entire app from crashing
 * - Display a controlled fallback UI
 *
 * IMPORTANT LIMITATIONS:
 * This boundary ONLY catches errors thrown during React rendering.
 *
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  /**
   * React lifecycle method triggered when a child throws during render
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Called after the error has been rendered to the fallback UI
   * Safe place for side effects (logging, monitoring)
   */
  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  /**
   * Automatically reset the boundary when resetKeys change
   * Example use case: route change resets previous error state
   */
  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const prevKeys = prevProps.resetKeys ?? [];
    const nextKeys = this.props.resetKeys ?? [];

    if (this.state.hasError && !shallowEqual(prevKeys, nextKeys)) {
      this.resetError();
    }
  }

  /**
   * Manual reset function exposed to fallback UI
   */
  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;

    if (hasError && error) {
      return this.props.fallback({
        error,
        resetError: this.resetError,
      });
    }

    return this.props.children;
  }
}

/**
 * Shallow comparison
 * Helper function to compare two arrays of unknown values for equality
 */
function shallowEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => Object.is(value, b[index]));
}
